import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { BloomingDataService } from '../../modules/report/services/blooming-data.service';
import { TopListService } from 'src/app/modules/report/services/toplist.service';
import { TopPlantsService } from 'src/app/modules/report/services/topplants.service';
import { ResultSelectors } from './result.selectors';
import { ResultActions } from './result.actions';
import { subMonths } from 'date-fns';

@Injectable()
export class ResultEffects {
  fetchRecentBloomingData$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchRecentBloomingData),
      exhaustMap(() => {
        const today = new Date();
        const from = subMonths(today, 1);
        return this.bloomingData.getItems(from).pipe(
          map((response) =>
            ResultActions.fetchRecentBloomingDataOk({
              items: response,
            })
          ),
          catchError((err) => of(ResultActions.fetchRecentBloomingDataFail({ error: err })))
        )
      })
    )
  );

  fetchLivedata$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchLivedata),
      withLatestFrom(this.store.select(ResultSelectors.getLivedataQuery), this.store.select(ResultSelectors.getLivedataKey)),
      exhaustMap(([_, query, queryKey]) =>
        this.bloomingData.getLivedata(query.year).pipe(
          map((response) =>
            ResultActions.fetchLivedataOk({
              queryKey,
              items: response,
            })
          ),
          catchError((err) => of(ResultActions.fetchLivedataFail({ queryKey, error: err })))
        )
      )
    )
  );

  /**
   * Load user stats (toplist).
   */
  fetchTopListCache$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopListCache),
      withLatestFrom(this.store.select(ResultSelectors.getTopListCacheFlag)),
      map(([action, cacheFlag]) =>
        cacheFlag ? ResultActions.fetchTopListCacheHit() : ResultActions.fetchTopList()
      )
    )
  );

  // After we read from cache, we fetch again
  // TODO: testing this - does it make sense ?
  fetchTopListAfterCache$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopListCacheHit),
      map(() => ResultActions.fetchTopList())
    )
  );

  fetchTopList$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopList),
      exhaustMap(() =>
        this.toplistData.getItems().pipe(
          map((response) =>
            ResultActions.fetchTopListOk({
              items: response,
            })
          ),
          catchError((err) => of(ResultActions.fetchTopListFail({ error: err })))
        )
      )
    )
  );

  /**
  * Load top plants.
  */
  fetchTopPlantsCache$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopPlantsCache),
      withLatestFrom(this.store.select(ResultSelectors.getTopListCacheFlag)),
      map(([action, cacheFlag]) =>
        cacheFlag ? ResultActions.fetchTopPlantsCacheHit() : ResultActions.fetchTopPlants()
      )
    )
  );

  // After we read from cache, we fetch again
  // TODO: testing this - does it make sense ?
  fetchTopPlantsAfterCache$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopPlantsCacheHit),
      map(() => ResultActions.fetchTopPlants())
    )
  );

  fetchTopPlants$ = createEffect(() =>
    this.actions.pipe(
      ofType(ResultActions.fetchTopPlants),
      exhaustMap(() =>
        this.topPlantsData.getItems().pipe(
          map((response) =>
            ResultActions.fetchTopPlantsOk({
              items: response,
            })
          ),
          catchError((err) => of(ResultActions.fetchTopPlantsFail({ error: err })))
        )
      )
    )
  );

  constructor(
    private actions: Actions,
    private bloomingData: BloomingDataService,
    private toplistData: TopListService,
    private topPlantsData: TopPlantsService,
    private store: Store,
  ) { }
}
